import React, { useState, useEffect, useCallback } from 'react';
import { Heading } from './heading';
import { Input } from './input';
import { Textarea } from './textarea';
import { Field, Label } from './fieldset';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Text } from './text';
import {
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/solid';

const VALUE_TYPES = [
  { id: 'string', label: 'Texto', example: '"string"', description: 'Texto simple' },
  { id: 'string|null', label: 'Texto opcional', example: '"string"|null', description: 'Texto que puede ser nulo' },
  { id: 'integer', label: 'Número entero', example: '0', description: 'Números sin decimales' },
  { id: 'integer|null', label: 'Número entero opcional', example: '0|null', description: 'Número entero que puede ser nulo' },
  { id: 'number', label: 'Número decimal', example: '0.0', description: 'Números con decimales' },
  { id: 'number|null', label: 'Número decimal opcional', example: '0.0|null', description: 'Número decimal que puede ser nulo' },
  { id: 'boolean', label: 'Verdadero/Falso', example: 'true|false', description: 'Valor booleano' },
  { id: 'array<string>', label: 'Lista de textos', example: '["string"]', description: 'Array de textos' },
  { id: 'array<object>', label: 'Lista de objetos', example: '[{}]', description: 'Array de objetos complejos' },
  { id: 'object', label: 'Objeto', example: '{}', description: 'Objeto JSON anidado' }
];

// Constants for guidelines and validation
const GUIDELINES = [
  'Extract information exactly as written — do not infer or assume missing details.',
  'Focus on descriptive and factual data.',
  'Return only one JSON object following the structure below.'
];

const VALIDATION_RULES = [
  'Do not fill missing fields.',
  'Preserve original language (Spanish or English).'
];

export default function PromptBuilder({ pdfFile, pdfUrl, onHighlightLines, onProcessedData }) {
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [sourceIndex, setSourceIndex] = useState({});
  const [guidelines, setGuidelines] = useState([...GUIDELINES]);
  const [validationRules, setValidationRules] = useState([...VALIDATION_RULES]);
  const [fields, setFields] = useState([
    {
      id: Date.now(),
      fieldName: 'NombreEmpresa',
      valueType: 'string',
      comment: 'Company name as written'
    }
  ]);

  const addField = () => {
    setFields([
      ...fields,
      {
        id: Date.now(),
        fieldName: '',
        valueType: 'string',
        comment: ''
      }
    ]);
  };

  const removeField = (id) => {
    setFields(fields.filter(f => f.id !== id));
  };

  const updateField = (id, key, value) => {
    setFields(fields.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < fields.length) {
      [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
      setFields(newFields);
    }
  };

  const addGuideline = () => {
    setGuidelines([...guidelines, '']);
  };

  const removeGuideline = (index) => {
    setGuidelines(guidelines.filter((_, i) => i !== index));
  };

  const updateGuideline = (index, value) => {
    setGuidelines(guidelines.map((g, i) => i === index ? value : g));
  };

  const addValidationRule = () => {
    setValidationRules([...validationRules, '']);
  };

  const removeValidationRule = (index) => {
    setValidationRules(validationRules.filter((_, i) => i !== index));
  };

  const updateValidationRule = (index, value) => {
    setValidationRules(validationRules.map((r, i) => i === index ? value : r));
  };

  const generatePrompt = () => {
    const guidelinesText = guidelines.map((g, i) => `${i + 1}. ${g}`).join('\n');
    const validationText = validationRules.map(r => `- ${r}`).join('\n');

    const jsonStructure = fields.map(field => {
      let valueExample;
      if (field.valueType.startsWith('array<')) {
        valueExample = `[${field.valueType.replace('array<', '').replace('>', '')}]`;
      } else {
        valueExample = field.valueType;
      }

      return `    "${field.fieldName}": {
        "value": ${valueExample},  // ${field.comment}
        "sources": ["line:X"]
    }`;
    }).join(',\n');

    return `"""
You are a highly specialized analyst focused on processing "${documentType}" documents. ${description}

IMPORTANT GUIDELINES:
${guidelinesText}

The provided text is structured with line markers [line:X]...[/line:X]. Extract the information into this JSON structure:

{
${jsonStructure}
}

VALIDATION RULES:
${validationText}

Context:

"""`;
  };

  const copyToClipboard = () => {
    const prompt = generatePrompt();
    navigator.clipboard.writeText(prompt);
  };

  const processWithPrompt = async () => {
    if (!pdfFile) {
      console.error('No file provided');
      alert('Por favor sube un archivo PDF primero');
      return;
    }

    setIsProcessing(true);

    const prompt = generatePrompt();
    const formData = new FormData();
    formData.append('file', pdfFile);
    formData.append('prompt', prompt);

    try {
      console.log('Sending request to process file with custom prompt...');
      console.log('File:', pdfFile.name);
      console.log('Prompt length:', prompt.length);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/process-with-custom-prompt`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.status === 'success') {
        console.log('=== PROCESSING RESULTS ===');
        console.log('Results:', result.data.results);
        console.log('Lines:', result.data.lines);
        console.log('Total Lines Extracted:', result.data.totalLinesExtracted);
        console.log('Lines Referenced:', result.data.linesReferenced);
        console.log('=========================');

        // Store results to display
        setResults(result.data);

        // Pass processed data to parent for highlighting
        if (onProcessedData) {
          onProcessedData(result.data);
        }
      } else {
        console.error('Processing failed:', result.error);
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      alert(`Error procesando archivo: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const [activeTab, setActiveTab] = useState('structured'); // 'structured' or 'chat'

  // Chat state
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatSourceIndex, setChatSourceIndex] = useState({});

  // Start chat session
  const startChatSession = useCallback(async () => {
    if (!pdfFile || chatSessionId) return;

    setIsChatLoading(true);
    const formData = new FormData();
    formData.append('file', pdfFile);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/start-document-chat`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.status === 'success') {
        setChatSessionId(result.data.sessionId);
        if (onProcessedData) {
          onProcessedData(result.data);
        }
      } else {
        alert('Error iniciando sesión de chat');
      }
    } catch (error) {
      console.error('Error starting chat session:', error);
      alert(`Error iniciando chat: ${error.message}`);
    } finally {
      setIsChatLoading(false);
    }
  }, [pdfFile, chatSessionId, onProcessedData]);

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || !chatSessionId || isChatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');

    // Add user message to chat
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/chat-with-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: chatSessionId,
          message: userMessage
        })
      });

      const result = await response.json();

      if (result.status === 'success') {
        // Add assistant message to chat
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: result.data.answer,
          lines: result.data.lines,
          totalLinesReferenced: result.data.totalLinesReferenced
        }]);

        // Update processed lines for highlighting
        if (onProcessedData && result.data.lines) {
          onProcessedData({ lines: result.data.lines });
        }
      } else {
        alert('Error enviando mensaje');
      }
    } catch (error) {
      console.error('Error sending chat message:', error);
      alert(`Error en chat: ${error.message}`);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Handle chat source navigation
  const handleChatSourceClick = (messageIdx, lineIds) => {
    if (onHighlightLines && lineIds && lineIds.length > 0) {
      const currentIdx = chatSourceIndex[messageIdx] || 0;
      onHighlightLines([lineIds[currentIdx]]);
    }
  };

  const handleChatSourceNavigation = (messageIdx, direction, lineIds) => {
    const currentIdx = chatSourceIndex[messageIdx] || 0;
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentIdx === 0 ? lineIds.length - 1 : currentIdx - 1;
    } else {
      newIndex = currentIdx === lineIds.length - 1 ? 0 : currentIdx + 1;
    }

    setChatSourceIndex({ ...chatSourceIndex, [messageIdx]: newIndex });
    if (onHighlightLines) {
      onHighlightLines([lineIds[newIndex]]);
    }
  };

  // Initialize chat session when switching to chat tab
  useEffect(() => {
    if (activeTab === 'chat' && results && !chatSessionId && pdfFile) {
      startChatSession();
    }
  }, [activeTab, results, chatSessionId, pdfFile, startChatSession]);

  // If results are available, show results view
  if (results) {
    return (
      <div className="space-y-4 p-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('structured')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'structured'
                ? 'text-azul-surelia border-b-2 border-azul-surelia'
                : 'text-gray-600 hover:text-azul-surelia'
            }`}
          >
            Extracción Estructurada
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'chat'
                ? 'text-azul-surelia border-b-2 border-azul-surelia'
                : 'text-gray-600 hover:text-azul-surelia'
            }`}
          >
            Chat con Documento
          </button>
        </div>

        {activeTab === 'structured' ? (
      <div className="space-y-4">
        {/* Back button */}
        <button
          onClick={() => setResults(null)}
          className="flex items-center gap-2 text-azul-surelia hover:text-azul-surelia/80 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Volver al Prompt Builder
        </button>

        {/* Results Header */}
        <div>
          <Heading level={2} className="text-azul-surelia mb-2">
            Resultados de Extracción
          </Heading>
          <Text className="text-gray-600 text-sm">
            {results.linesReferenced} de {results.totalLinesExtracted} líneas referenciadas
          </Text>
        </div>

        {/* Divider */}
        <div className="flex justify-center py-2">
          <div className="w-32 border-t-2 border-gray-300"></div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Campo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(results.results).map(([key, data]) => {
                const isSelected = selectedField === key;
                const hasSources = data.sources && data.sources.length > 0;

                const handleSourceNavigation = (fieldKey, direction, sources) => {
                  const currentIdx = sourceIndex[fieldKey];

                  if (currentIdx === undefined) {
                    // First click - show first source
                    setSourceIndex({ ...sourceIndex, [fieldKey]: 0 });
                    if (onHighlightLines) {
                      onHighlightLines([sources[0]]);
                    }
                  } else {
                    // Navigate to next/prev
                    let newIndex;
                    if (direction === 'prev') {
                      newIndex = currentIdx === 0 ? sources.length - 1 : currentIdx - 1;
                    } else {
                      newIndex = currentIdx === sources.length - 1 ? 0 : currentIdx + 1;
                    }
                    setSourceIndex({ ...sourceIndex, [fieldKey]: newIndex });
                    if (onHighlightLines) {
                      onHighlightLines([sources[newIndex]]);
                    }
                  }
                  setSelectedField(fieldKey);
                };

                return (
                  <tr
                    key={key}
                    className={`transition-colors ${isSelected ? 'bg-azul-surelia/10' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap">
                      {key}
                      {hasSources && data.sources.length > 1 && (
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            onClick={() => handleSourceNavigation(key, 'prev', data.sources)}
                            className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                            title="Fuente anterior"
                          >
                            <ChevronLeftIcon className="h-3 w-3 text-azul-surelia" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedField(key);
                              if (onHighlightLines) {
                                const idx = sourceIndex[key] !== undefined ? sourceIndex[key] : 0;
                                onHighlightLines([data.sources[idx]]);
                              }
                            }}
                            className="text-xs font-medium text-azul-surelia px-1 hover:bg-azul-surelia/10 rounded transition-colors cursor-pointer flex items-center gap-0.5"
                            title="Ver fuente actual"
                          >
                            <MapPinIcon className="h-3 w-3" />
                            {(sourceIndex[key] !== undefined ? sourceIndex[key] : 0) + 1}/{data.sources.length}
                          </button>
                          <button
                            onClick={() => handleSourceNavigation(key, 'next', data.sources)}
                            className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                            title="Fuente siguiente"
                          >
                            <ChevronRightIcon className="h-3 w-3 text-azul-surelia" />
                          </button>
                        </div>
                      )}
                      {hasSources && data.sources.length === 1 && (
                        <button
                          onClick={() => {
                            setSelectedField(key);
                            if (onHighlightLines) {
                              onHighlightLines(data.sources);
                            }
                          }}
                          className="flex items-center gap-1 mt-1 text-xs font-medium text-azul-surelia hover:bg-azul-surelia/10 rounded px-1 transition-colors"
                        >
                          <MapPinIcon className="h-3 w-3" />
                          Ver fuente
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-words">
                      {Array.isArray(data.value) ? (
                        <ul className="list-disc list-inside space-y-1">
                          {data.value.map((item, idx) => (
                            <li key={idx} className="break-words">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="break-words">
                          {data.value || <span className="text-gray-400 italic">null</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
        ) : (
          // Chat Tab
          <div className="space-y-4 h-full flex flex-col">
            {/* Back button */}
            <button
              onClick={() => setResults(null)}
              className="flex items-center gap-2 text-azul-surelia hover:text-azul-surelia/80 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Volver al Prompt Builder
            </button>

            {/* Chat Header */}
            <div>
              <Heading level={2} className="text-azul-surelia mb-2">
                Chat con Documento
              </Heading>
              <Text className="text-gray-600 text-sm">
                {chatSessionId ? 'Sesión iniciada - Haz preguntas sobre el documento' : 'Iniciando sesión de chat...'}
              </Text>
            </div>

            {/* Divider */}
            <div className="flex justify-center py-2">
              <div className="w-32 border-t-2 border-gray-300"></div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 bg-white rounded-lg border overflow-y-auto p-4 space-y-4 min-h-[400px]">
              {chatMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <Text className="text-gray-400 italic">
                    Escribe un mensaje para comenzar la conversación
                  </Text>
                </div>
              ) : (
                chatMessages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-azul-surelia text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <Text className={message.role === 'user' ? 'text-white' : 'text-gray-900'}>
                        {message.content}
                      </Text>

                      {/* Show sources for assistant messages */}
                      {message.role === 'assistant' && message.lines && Object.keys(message.lines).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-300">
                          <Text className="text-xs text-gray-600 mb-1">
                            {message.totalLinesReferenced} línea{message.totalLinesReferenced !== 1 ? 's' : ''} referenciada{message.totalLinesReferenced !== 1 ? 's' : ''}
                          </Text>
                          <div className="flex items-center gap-1">
                            {Object.keys(message.lines).length > 1 ? (
                              <>
                                <button
                                  onClick={() => handleChatSourceNavigation(idx, 'prev', Object.keys(message.lines))}
                                  className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                                  title="Fuente anterior"
                                >
                                  <ChevronLeftIcon className="h-3 w-3 text-azul-surelia" />
                                </button>
                                <button
                                  onClick={() => handleChatSourceClick(idx, Object.keys(message.lines))}
                                  className="text-xs font-medium text-azul-surelia px-1 hover:bg-azul-surelia/10 rounded transition-colors cursor-pointer flex items-center gap-0.5"
                                  title="Ver fuente actual"
                                >
                                  <MapPinIcon className="h-3 w-3" />
                                  {(chatSourceIndex[idx] || 0) + 1}/{Object.keys(message.lines).length}
                                </button>
                                <button
                                  onClick={() => handleChatSourceNavigation(idx, 'next', Object.keys(message.lines))}
                                  className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                                  title="Fuente siguiente"
                                >
                                  <ChevronRightIcon className="h-3 w-3 text-azul-surelia" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  if (onHighlightLines) {
                                    onHighlightLines(Object.keys(message.lines));
                                  }
                                }}
                                className="flex items-center gap-1 text-xs font-medium text-azul-surelia hover:bg-azul-surelia/10 rounded px-1 transition-colors"
                              >
                                <MapPinIcon className="h-3 w-3" />
                                Ver fuente
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {/* Loading indicator */}
              {isChatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <Text className="text-gray-600 italic">Escribiendo...</Text>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                placeholder="Escribe tu pregunta sobre el documento..."
                className="flex-1"
                disabled={!chatSessionId || isChatLoading}
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatSessionId || isChatLoading || !chatInput.trim()}
                className="px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                Enviar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      {/* Tabs - Always visible */}
      <div className="flex gap-2 border-b mb-4">
        <button
          onClick={() => setActiveTab('structured')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'structured'
              ? 'text-azul-surelia border-b-2 border-azul-surelia'
              : 'text-gray-600 hover:text-azul-surelia'
          }`}
        >
          Extracción Estructurada
        </button>
        <button
          onClick={() => {
            setActiveTab('chat');
            if (pdfFile && !chatSessionId) {
              startChatSession();
            }
          }}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'chat'
              ? 'text-azul-surelia border-b-2 border-azul-surelia'
              : 'text-gray-600 hover:text-azul-surelia'
          }`}
        >
          Chat con Documento
        </button>
      </div>

      {/* Show chat interface if chat tab is active and no results yet */}
      {activeTab === 'chat' && !results ? (
        <div className="space-y-4 h-full flex flex-col">
          {/* Chat Header */}
          <div>
            <Heading level={2} className="text-azul-surelia mb-2">
              Chat con Documento
            </Heading>
            <Text className="text-gray-600 text-sm">
              {!pdfFile
                ? 'Por favor, sube un archivo PDF para comenzar'
                : chatSessionId
                  ? 'Sesión iniciada - Haz preguntas sobre el documento'
                  : 'Iniciando sesión de chat...'
              }
            </Text>
          </div>

          {/* Divider */}
          <div className="flex justify-center py-2">
            <div className="w-32 border-t-2 border-gray-300"></div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 bg-white rounded-lg border overflow-y-auto p-4 space-y-4 min-h-[400px]">
            {chatMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Text className="text-gray-400 italic">
                  {!pdfFile
                    ? 'Sube un archivo PDF para comenzar a chatear'
                    : 'Escribe un mensaje para comenzar la conversación'
                  }
                </Text>
              </div>
            ) : (
              chatMessages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-azul-surelia'
                        : 'bg-gray-100'
                    }`}
                  >
                    <div className={message.role === 'user' ? 'text-white' : 'text-gray-900'}>
                      {message.content}
                    </div>

                    {/* Show sources for assistant messages */}
                    {message.role === 'assistant' && message.lines && Object.keys(message.lines).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-300">
                        <Text className="text-xs text-gray-600 mb-1">
                          {message.totalLinesReferenced} línea{message.totalLinesReferenced !== 1 ? 's' : ''} referenciada{message.totalLinesReferenced !== 1 ? 's' : ''}
                        </Text>
                        <div className="flex items-center gap-1">
                          {Object.keys(message.lines).length > 1 ? (
                            <>
                              <button
                                onClick={() => handleChatSourceNavigation(idx, 'prev', Object.keys(message.lines))}
                                className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Fuente anterior"
                              >
                                <ChevronLeftIcon className="h-3 w-3 text-azul-surelia" />
                              </button>
                              <button
                                onClick={() => handleChatSourceClick(idx, Object.keys(message.lines))}
                                className="text-xs font-medium text-azul-surelia px-1 hover:bg-azul-surelia/10 rounded transition-colors cursor-pointer flex items-center gap-0.5"
                                title="Ver fuente actual"
                              >
                                <MapPinIcon className="h-3 w-3" />
                                {(chatSourceIndex[idx] || 0) + 1}/{Object.keys(message.lines).length}
                              </button>
                              <button
                                onClick={() => handleChatSourceNavigation(idx, 'next', Object.keys(message.lines))}
                                className="p-0.5 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Fuente siguiente"
                              >
                                <ChevronRightIcon className="h-3 w-3 text-azul-surelia" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                if (onHighlightLines) {
                                  onHighlightLines(Object.keys(message.lines));
                                }
                              }}
                              className="flex items-center gap-1 text-xs font-medium text-azul-surelia hover:bg-azul-surelia/10 rounded px-1 transition-colors"
                            >
                              <MapPinIcon className="h-3 w-3" />
                              Ver fuente
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Loading indicator */}
            {isChatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <Text className="text-gray-600 italic">Escribiendo...</Text>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input */}
          <div className="flex gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage();
                }
              }}
              placeholder="Escribe tu pregunta sobre el documento..."
              className="flex-1"
              disabled={!chatSessionId || isChatLoading}
            />
            <button
              onClick={sendChatMessage}
              disabled={!chatSessionId || isChatLoading || !chatInput.trim()}
              className="px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              Enviar
            </button>
          </div>
        </div>
      ) : activeTab === 'structured' && !results ? (
        <>
      {/* Basic Info */}
      <div>
        <Heading level={2} className="text-azul-surelia mb-4">
          Información Básica del Documento
        </Heading>
        <div className="space-y-3">
          <Field>
            <Label>Tipo de Documento</Label>
            <Input
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              placeholder="Acta Constitutiva"
            />
          </Field>
          <Field>
            <Label>Descripción</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Documento notarial que avala la creación de una persona moral en México y..."
              rows={3}
            />
          </Field>
        </div>
      </div>

      {/* Divider */}
      <div className="flex justify-center py-2">
        <div className="w-32 border-t-2 border-gray-300"></div>
      </div>

      {/* Fields */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Heading level={2} className="text-azul-surelia">
            Campos de Extracción
          </Heading>
          <button
            onClick={addField}
            className="px-3 py-1.5 bg-azul-surelia text-white text-sm rounded-lg hover:bg-azul-surelia/90 transition-colors"
          >
            +
          </button>
        </div>
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
              <div className="p-3">
                <div className="flex items-start gap-2">
                  {/* Order controls */}
                  <div className="flex flex-col gap-1 pt-1">
                    <button
                      onClick={() => moveField(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Mover arriba"
                    >
                      <ArrowUpIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => moveField(index, 'down')}
                      disabled={index === fields.length - 1}
                      className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Mover abajo"
                    >
                      <ArrowDownIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>

                  {/* Field content */}
                  <div className="flex-1 space-y-2">
                    {/* Field name and type in one row */}
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={field.fieldName}
                        onChange={(e) => updateField(field.id, 'fieldName', e.target.value)}
                        placeholder="Nombre del campo (ej: NombreEmpresa)"
                      />
                      <select
                        value={field.valueType}
                        onChange={(e) => updateField(field.id, 'valueType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
                      >
                        {VALUE_TYPES.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Comment */}
                    <Input
                      value={field.comment}
                      onChange={(e) => updateField(field.id, 'comment', e.target.value)}
                      placeholder="Descripción / comentario (ej: Company name as written)"
                    />
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={() => removeField(field.id)}
                    className="p-2 hover:bg-red-50 rounded transition-colors mt-1"
                    title="Eliminar campo"
                  >
                    <TrashIcon className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="flex justify-center py-2">
        <div className="w-32 border-t-2 border-gray-300"></div>
      </div>

      {/* Guidelines - Collapsible & Editable */}
      <div>
        <button
          onClick={() => setShowGuidelines(!showGuidelines)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Heading level={3} className="text-azul-surelia text-sm">
            Lineamientos Importantes
          </Heading>
          {showGuidelines ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
        {showGuidelines && (
          <div className="mt-2 px-3 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              {guidelines.map((guideline, index) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="text-sm text-gray-600 mt-2 min-w-[20px]">{index + 1}.</span>
                  <Textarea
                    value={guideline}
                    onChange={(e) => updateGuideline(index, e.target.value)}
                    className="flex-1"
                    placeholder="Agregar lineamiento..."
                    rows={2}
                  />
                  <button
                    onClick={() => removeGuideline(index)}
                    className="p-2 hover:bg-red-50 rounded transition-colors mt-1"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addGuideline}
              className="mt-2 w-full px-3 py-1.5 bg-azul-surelia/10 text-azul-surelia text-sm rounded-lg hover:bg-azul-surelia/20 transition-colors"
            >
              + Agregar Lineamiento
            </button>
          </div>
        )}
      </div>

      {/* Validation Rules - Collapsible & Editable */}
      <div>
        <button
          onClick={() => setShowValidation(!showValidation)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Heading level={3} className="text-azul-surelia text-sm">
            Reglas de Validación
          </Heading>
          {showValidation ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
        {showValidation && (
          <div className="mt-2 px-3 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              {validationRules.map((rule, index) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="text-sm text-gray-600 mt-2">•</span>
                  <Textarea
                    value={rule}
                    onChange={(e) => updateValidationRule(index, e.target.value)}
                    className="flex-1"
                    placeholder="Agregar regla..."
                    rows={2}
                  />
                  <button
                    onClick={() => removeValidationRule(index)}
                    className="p-2 hover:bg-red-50 rounded transition-colors mt-1"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addValidationRule}
              className="mt-2 w-full px-3 py-1.5 bg-azul-surelia/10 text-azul-surelia text-sm rounded-lg hover:bg-azul-surelia/20 transition-colors"
            >
              + Agregar Regla
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="flex justify-center py-2">
        <div className="w-32 border-t-2 border-gray-300"></div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => setIsDialogOpen(true)}
          className="px-6 py-3 bg-white border-2 border-azul-surelia text-azul-surelia rounded-lg hover:bg-azul-surelia/5 transition-colors flex items-center gap-2"
        >
          Ver Prompt
        </button>
        <button
          onClick={processWithPrompt}
          disabled={!pdfFile || isProcessing}
          className="px-6 py-3 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Procesando...' : 'Procesar'}
        </button>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} size="5xl">
        <DialogTitle>Prompt Generado</DialogTitle>
        <DialogBody>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto max-h-[60vh] overflow-y-auto">
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {generatePrompt()}
            </pre>
          </div>
        </DialogBody>
        <DialogActions>
          <Button color="white" onClick={() => setIsDialogOpen(false)}>
            Cerrar
          </Button>
          <Button color="surelia" onClick={copyToClipboard}>
            Copiar al Portapapeles
          </Button>
        </DialogActions>
      </Dialog>
        </>
      ) : null}
    </div>
  );
}
